import {
  BulkActionCount,
  DisplayFieldErrors,
  FormattedDate,
  ViewContext,
  useTableSelection,
} from "melco-shared-logic";
import isEmpty from "lodash-es/isEmpty";
import {
  Button,
  DesignTokens,
  FullWidthSpace,
  PlusIcon,
  Space,
  Table,
  Title,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TFunction } from "i18next";
import dayjs from "dayjs";
import { useField } from "formik";
import { BulkActionRemoveMachines } from "./BulkActionRemoveMachines";

const { colors, spacings } = DesignTokens;

const EmptyMachineWrapper = styled.div`
  border: 1px dashed ${colors.gray.middle};
  padding: ${spacings.middle}px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TableWrapper = styled.div`
  margin-left: -${spacings.xlarge}px;
  margin-right: -${spacings.xlarge}px;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

type MachineListProps = {
  setDrawerOpen: (open: boolean) => void;
};

export const machineListColumns = (
  t: TFunction,
  context: ViewContext = ViewContext.List
) => {
  // use local sorting for ViewContext.List
  const sorter = (dataIndex: string) => {
    if (context === ViewContext.List) {
      return (a: any, b: any) =>
        String(a[dataIndex]).localeCompare(String(b[dataIndex]));
    }

    return true;
  };

  const dateSorter = (dataIndex: string) => {
    if (context === ViewContext.List) {
      return (a: any, b: any) =>
        dayjs(a[dataIndex]).unix() - dayjs(b[dataIndex]).unix();
    }
    return true;
  };

  return [
    {
      title: t("reporting.machines.list.columns.serial_number"),
      key: "serial_number",
      dataIndex: "serial_number",
      sorter: sorter("serial_number"),
    },
    {
      title: t("reporting.machines.list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: sorter("name"),
    },
    {
      title: t("reporting.machines.list.columns.registered"),
      key: "registered_date",
      sorter: dateSorter("registered_date"),
      render: (item: any) => <FormattedDate date={item.registered_date} />,
    },
    {
      title: t("reporting.machines.list.columns.last_sync"),
      key: "last_sync_date",
      //dataIndex: "last_sync_date",
      sorter: dateSorter("last_sync_date"),
      render: (item: any) => <FormattedDate date={item.last_sync_date} />,
    },
  ];
};

export const MachineList: React.FC<MachineListProps> = ({ setDrawerOpen }) => {
  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection } =
    useTableSelection<any>(() => {});

  const [{ value: machines }] = useField<any[]>("machine_list");
  const title = (
    <Title level={5}>{t("reporting.machines.title_assigned_machines")}</Title>
  );

  const addButton = (
    <Button
      type="primary"
      icon={<PlusIcon />}
      onClick={() => setDrawerOpen(true)}
    >
      {t("reporting.machines.button")}
    </Button>
  );

  if (isEmpty(machines)) {
    return (
      <>
        {title}
        <DisplayFieldErrors name="machine_list" />
        {/* <DisplayFieldErrors name="name" /> */}

        <EmptyMachineWrapper>{addButton}</EmptyMachineWrapper>
      </>
    );
  }

  const columns = [...machineListColumns(t)];

  return (
    <>
      <TableHeader>
        {title}

        <Space size="middle">
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionRemoveMachines
            //  allMachine={machines}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </Space>
      </TableHeader>

      <DisplayFieldErrors name="machine_list" />
      {/* <DisplayFieldErrors name="name" /> */}

      <FullWidthSpace direction="vertical" size="large">
        <TableWrapper>
          <Table
            showSorterTooltip={false}
            dataSource={machines}
            columns={columns}
            rowKey="device_id"
            pagination={false}
            rowSelection={rowSelection}
            data-cy="machine-list-table"
            noDataMessage={t("reporting.machines.list.empty")}
          />
        </TableWrapper>
        <AddButtonWrapper>{addButton}</AddButtonWrapper>
      </FullWidthSpace>
    </>
  );
};
